/**
 * Permissions — the finer view of what a membership may do.
 *
 * ## What this adds, and what it deliberately does not replace
 *
 * A membership already carries an `OrgRole`. That role is the **coarse** answer: it ranks
 * (`ROLE_RANK`), it gates every route in the product through `requireRole`/`atLeast`, and it is
 * what the client auto-login links hand out (`ClientAccessRole`). None of that changes here.
 *
 * What a role cannot express is the thing the product actually needs: "this person may schedule a
 * post but somebody else has to approve it" and "this person approves, and writes nothing". Those
 * are two independent grants on the same module, held by different people, and no ordering of
 * five role names produces them. So a membership may additionally carry a **Role row** — a named
 * set of these permission keys, defined by the workspace — and the keys below are what such a row
 * can say.
 *
 * ## Keys are booleans, not an ordered level
 *
 * The obvious modelling is `Record<module, 'NONE' | 'VIEW' | 'EDIT'>`. It is wrong for the one
 * part of the matrix that matters most. `planner.publishWithoutReview`, `planner.scheduleForReview`
 * and `planner.reviewPosts` are three grants that do not sit on a line: a reviewer typically holds
 * the third and neither of the first two, and a content creator holds the second and not the
 * first. Collapsing them into a level would either lose one of them or invent a ranking nobody
 * asked for. So every key is an independent boolean, exactly as the checkbox matrix it mirrors,
 * and the *tiering* lives in `PERMISSION_TIER` — presentation, not semantics.
 *
 * Where a level genuinely exists it is expressed as **implication** rather than as an ordinal:
 * `inbox.edit` implies `inbox.view` (see `IMPLIES`), resolved once in `resolvePermissions` so no
 * caller has to remember that granting the edit key without the view key is nonsense.
 *
 * ## Why a typed JSON bag rather than a join table
 *
 * Same reason `PlanFeatures` is one: the set of keys is fixed and owned by the code. Adding a
 * permission is a deploy, never a row, so a join table would buy the ability to store keys the
 * code cannot read — and would need a migration anyway to become meaningful. `readPermissions`
 * mirrors `readFeatures` in `modules/billing/plan-mapper.ts`: unknown keys are dropped, absent
 * keys take the default, so a role row written by an older deploy stays readable by a newer one.
 */

import type { OrgRole } from './types.js'

/* ── the keys ──────────────────────────────────────────────────────────────── */

/**
 * Every permission a Role row can grant. Ordered as the editor renders them.
 *
 * The names are `module.capability`, and the capability half is the tier: `view` reads,
 * everything else writes. `brand.manage` is the third tier on its own — brand configuration,
 * its connections, and who else may see it.
 */
export const PERMISSION_KEYS = [
  'analytics.view',
  'inbox.view',
  'planner.view',
  'ads.view',
  'smartlinks.view',
  'studio.view',
  'campaigns.view',
  'inbox.edit',
  'planner.publishWithoutReview',
  'planner.scheduleForReview',
  'planner.reviewPosts',
  'ads.edit',
  'smartlinks.edit',
  'studio.edit',
  'campaigns.edit',
  'brand.manage',
] as const

export type PermissionKey = (typeof PERMISSION_KEYS)[number]

/** A complete answer: every key, decided. Never partial — see `resolvePermissions`. */
export type PermissionSet = Record<PermissionKey, boolean>

/** What a partially-written role row looks like on the wire and in the database column. */
export type PartialPermissionSet = Partial<Record<PermissionKey, boolean>>

/* ── tiers ─────────────────────────────────────────────────────────────────── */

export type PermissionTier = 'view' | 'edit' | 'manage'

/** The three groups the role editor draws, and nothing else depends on. */
export const PERMISSION_TIER: Record<PermissionKey, PermissionTier> = {
  'analytics.view': 'view',
  'inbox.view': 'view',
  'planner.view': 'view',
  'ads.view': 'view',
  'smartlinks.view': 'view',
  'studio.view': 'view',
  'campaigns.view': 'view',
  'inbox.edit': 'edit',
  'planner.publishWithoutReview': 'edit',
  'planner.scheduleForReview': 'edit',
  'planner.reviewPosts': 'edit',
  'ads.edit': 'edit',
  'smartlinks.edit': 'edit',
  'studio.edit': 'edit',
  'campaigns.edit': 'edit',
  'brand.manage': 'manage',
}

export const PERMISSION_TIERS: readonly PermissionTier[] = ['view', 'edit', 'manage']

/** The keys of one tier, in catalogue order. */
export const permissionsInTier = (tier: PermissionTier): PermissionKey[] =>
  PERMISSION_KEYS.filter((key) => PERMISSION_TIER[key] === tier)

/* ── implication ───────────────────────────────────────────────────────────── */

/**
 * "Granting this key also grants these."
 *
 * Only ever downward, only ever within one module, and never transitively deep — one pass in
 * `resolvePermissions` is enough because no implied key implies anything itself. `brand.manage`
 * implies nothing on purpose: managing a brand's configuration is not permission to read its
 * inbox, and pretending otherwise would quietly widen the most powerful key in the set.
 */
const IMPLIES: Partial<Record<PermissionKey, readonly PermissionKey[]>> = {
  'inbox.edit': ['inbox.view'],
  'ads.edit': ['ads.view'],
  'smartlinks.edit': ['smartlinks.view'],
  'studio.edit': ['studio.view'],
  'campaigns.edit': ['campaigns.view'],
  'planner.publishWithoutReview': ['planner.view'],
  'planner.scheduleForReview': ['planner.view'],
  'planner.reviewPosts': ['planner.view'],
}

const NOTHING: PermissionSet = PERMISSION_KEYS.reduce<PermissionSet>(
  (set, key) => ({ ...set, [key]: false }),
  {} as PermissionSet,
)

/** Every key false. A fresh custom role starts here. */
export const noPermissions = (): PermissionSet => ({ ...NOTHING })

/**
 * Fill in the absent keys and apply implication. The only way a `PermissionSet` is built.
 *
 * Unknown keys in `input` are ignored rather than rejected: this reads rows, and a row written by
 * a deploy that knew one more key must not become unreadable.
 */
export const resolvePermissions = (input: PartialPermissionSet): PermissionSet => {
  const set = noPermissions()
  for (const key of PERMISSION_KEYS) {
    if (input[key] === true) set[key] = true
  }
  for (const key of PERMISSION_KEYS) {
    if (!set[key]) continue
    for (const implied of IMPLIES[key] ?? []) set[implied] = true
  }
  return set
}

/**
 * Read a stored JSON column into a `PermissionSet`.
 *
 * Mirrors `readFeatures` in `modules/billing/plan-mapper.ts`: anything that is not an object of
 * booleans degrades to "no permissions" rather than throwing, because a malformed row must fail
 * shut and still be editable in the UI that will fix it.
 */
export const readPermissions = (value: unknown): PermissionSet => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return noPermissions()
  const input: PartialPermissionSet = {}
  for (const key of PERMISSION_KEYS) {
    const raw = (value as Record<string, unknown>)[key]
    if (typeof raw === 'boolean') input[key] = raw
  }
  return resolvePermissions(input)
}

/* ── the built-in roles ────────────────────────────────────────────────────── */

const grant = (...keys: readonly PermissionKey[]): PermissionSet =>
  resolvePermissions(Object.fromEntries(keys.map((key) => [key, true])))

const EVERY_VIEW = permissionsInTier('view')

/**
 * What each of the five built-in roles may do.
 *
 * This serves two purposes and it is worth keeping them apart:
 *
 *  1. **The effective permission set of a membership with no custom role.** Every membership in
 *     every existing workspace is in that state, so these sets are load-bearing for backwards
 *     compatibility: `BUILT_IN_PERMISSIONS.EDITOR['planner.publishWithoutReview']` is `true`
 *     precisely because `POST /brands/:id/posts/:id/publish` was `requireEditor` before this
 *     existed, and the route's answer may not change for anybody who has not opted in.
 *  2. **The starting point when a workspace duplicates a built-in into a role of its own.**
 *
 * `OWNER` and `ADMIN` hold everything, which is what they hold today. `EDITOR` holds everything
 * except `brand.manage` — brand-level administration is `requireOrgAdmin` today and stays there.
 * `ANALYST` is the read-everything role; `CLIENT` is the same minus the two surfaces that are
 * about running the workspace rather than about the client's own brand — the inbox is a
 * conversation with that brand's customers, and Studio is the agency's own dashboards.
 */
export const BUILT_IN_PERMISSIONS: Record<OrgRole, PermissionSet> = {
  OWNER: grant(...PERMISSION_KEYS),
  ADMIN: grant(...PERMISSION_KEYS),
  EDITOR: grant(
    ...EVERY_VIEW,
    'inbox.edit',
    'planner.publishWithoutReview',
    'planner.scheduleForReview',
    'planner.reviewPosts',
    'ads.edit',
    'smartlinks.edit',
    'studio.edit',
    'campaigns.edit',
  ),
  ANALYST: grant(...EVERY_VIEW),
  CLIENT: grant('analytics.view', 'planner.view', 'smartlinks.view', 'campaigns.view'),
}

/** The order the roles screen lists the presets in — most authority first, as `ROLE_RANK` ranks. */
export const BUILT_IN_ROLE_ORDER: readonly OrgRole[] = [
  'OWNER',
  'ADMIN',
  'EDITOR',
  'ANALYST',
  'CLIENT',
]

/* ── the wire shape ────────────────────────────────────────────────────────── */

/**
 * A role as `GET /api/v1/roles` returns it.
 *
 * Built-in roles are **synthesised from the constants above, not seeded as rows**. A seeded row
 * per workspace per preset would be five rows every org has to have, a backfill for every org
 * that already exists, and a way for one workspace's "EDITOR" to quietly stop meaning what
 * `requireEditor` means. Code constants cannot drift from the code.
 */
export interface RoleSummary {
  /** The row id for a custom role; the `OrgRole` name for a built-in one. */
  id: string
  name: string
  description: string | null
  /** Null for a built-in role. */
  orgId: string | null
  isCustom: boolean
  /**
   * The `OrgRole` this role ranks as. For a built-in role it *is* the role; for a custom one it
   * is the preset it was derived from, and the rank every un-migrated `requireRole` gate reads.
   */
  baseRole: OrgRole
  permissions: PermissionSet
  /** How many memberships hold it. Always 0 for a built-in role's synthesised entry. */
  memberCount: number
  createdAt: string | null
  updatedAt: string | null
}

/** The built-in entry for one `OrgRole`, in the same shape a custom role is returned in. */
export const builtInRoleSummary = (role: OrgRole): RoleSummary => ({
  id: role,
  name: role,
  description: null,
  orgId: null,
  isCustom: false,
  baseRole: role,
  permissions: { ...BUILT_IN_PERMISSIONS[role] },
  memberCount: 0,
  createdAt: null,
  updatedAt: null,
})
